// Sanitizer for the appointments landing page (plan §17.4). Markdown source in, safe
// markdown source out.
//
// This file mirrors app/src/helpers/landingPageMarkdown.js — the two must be kept in
// sync by hand, the same arrangement appointmentService and businessType already use.
// Unlike those, both copies are covered by tests (plan §15.6): this one by
// landingPageMarkdown.spec.ts beside it.
//
// The guest side stores nothing, so this copy exists for `safeLinkUrl`, which the
// renderer hands to react-markdown's `urlTransform`. Sanitize-on-write and safe-render
// are independent controls on purpose: either alone would do today, both means a
// mistake in one is not a vulnerability.

export const MAX_LANDING_PAGE_LENGTH = 5000;

const ALLOWED_LINK_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"];

// Tabs and newlines are stripped from a URL *anywhere* in it by the URL spec, so
// "java<tab>script:alert(1)" navigates while a naive startsWith("javascript:") check
// passes it. Removed before the protocol is read, never from the URL we hand back.
const URL_WHITESPACE = /\s/g;

// Anchored, so a target that does not *begin* with a recognisable scheme — a leading
// control character, "//evil.com", "/about", "#top" — matches nothing and is rejected.
const PROTOCOL = /^([a-z][a-z0-9+.-]*):/i;

// One level of nested parentheses in the target, which is what CommonMark allows and
// what `[x](javascript:alert(1))` needs. Matching the same span the parser will match
// matters: a regex that stopped at the first ")" would leave a stray tail behind in
// the source an owner reads back.
const TARGET = "((?:[^()]|\\([^()]*\\))*)";
const IMAGE = new RegExp("!\\[[^\\]]*\\]\\(" + TARGET + "\\)", "g");
const LINK = new RegExp("\\[([^\\]]*)\\]\\(" + TARGET + "\\)", "g");

/**
 * The href to render for a markdown link target, or "" when the target is not one of
 * the four protocols a landing page has any reason to use.
 *
 * A target with no protocol at all — "/about", "//evil.com", "#top" — is rejected too.
 * There is nothing useful to link to relative to /appointments/:id, and "//" is a real
 * vector rather than a hypothetical one.
 */
export function safeLinkUrl(url: unknown): string {
  if (typeof url !== "string") {
    return "";
  }
  const trimmed = url.trim();
  const match = PROTOCOL.exec(trimmed.replace(URL_WHITESPACE, ""));
  if (!match || !ALLOWED_LINK_PROTOCOLS.includes(match[1].toLowerCase() + ":")) {
    return "";
  }
  return trimmed;
}

/**
 * Rules, per §17.4: non-string -> ""; normalise line endings; strip HTML tags; strip
 * image syntax; reduce links with a disallowed target to their label text; collapse
 * runs of blank lines; trim; clamp.
 *
 * Images go before links rather than after, which is the one place this departs from
 * §17.4's stated order. The link rule would otherwise chew the `[alt](url)` half of an
 * `![alt](url)`, and the alternative — a negative lookbehind for `!` — throws at parse
 * time on Safari below 16.4, which is a browser the guest page still has to load in.
 * The outcome is identical: an image is removed either way.
 *
 * Stripping HTML is belt-and-braces — react-markdown ignores raw HTML without
 * rehype-raw. It is done so that what we *store* is clean, so a future renderer swap
 * cannot retroactively turn old rows into an XSS.
 */
export function sanitizeLandingPageMarkdown(input: unknown): string {
  if (typeof input !== "string") {
    return "";
  }

  return (
    input
      .replace(/\r\n?/g, "\n")
      .replace(/<[^>]*>/g, "")
      // Alt text goes with the image. A remote <img> on a guest page is a beacon — it
      // hands a third-party host the guest's IP and user agent on load — so the syntax
      // is not something to half-support.
      .replace(IMAGE, "")
      .replace(LINK, (whole, label, target) => (safeLinkUrl(target) ? whole : label))
      .replace(/\n{3,}/g, "\n\n")
      .trim()
      .slice(0, MAX_LANDING_PAGE_LENGTH)
  );
}
